/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'

import { EuiFlexGroup, EuiFlexItem, EuiText, EuiBadge } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { StackVersionConfig } from '@modules/cloud-api/v1/types'
import type { Region, AsyncRequestState, VersionNumber, RegionId } from '@modules/ui-types'
import { CuiTable } from '@modules/cui/Table'
import { CuiLink } from '@modules/cui/Link'
import usePermissions from '@modules/permissions-lib/hooks'

import { CuiSliderLogo } from '@/components/SliderLogo'
import EOLVersionWarningBadge from '@/components/EndOfLifeWarnings/EndOfLifeVersionWarningBadge'
import { getSupportedSliderInstanceTypes } from '@/lib/sliders/support'

import { elasticStackVersionUrl } from '../../../lib/urlBuilder'
import { rcompare, valid } from '../../../lib/semver'
import DangerButton from '../../DangerButton'

import WhitelistSwitch from './WhitelistSwitch'

import type { FunctionComponent } from 'react'

const deleteButtonStyles = css({
  // Allow buttons to have a small width
  '.euiButton': {
    minWidth: 'initial',
  },

  // Don't trim the top button border when it animates on hover
  '.euiTableCellContent__text': {
    overflow: 'initial',
  },
})

interface Props {
  region?: Region
  versions?: StackVersionConfig[]
  fetchVersions: (regionId: string) => Promise<unknown>
  deleteVersion: (version: string, region: Region) => Promise<unknown>
  getDeleteVersionRequest: (regionId: RegionId, version: VersionNumber) => AsyncRequestState
  isWhitelistingEnabled: boolean
}

const VersionTable: FunctionComponent<Props> = ({
  versions,
  region,
  fetchVersions,
  deleteVersion,
  getDeleteVersionRequest,
  isWhitelistingEnabled,
}) => {
  const sortedVersions = versions && versions.sort(compareVersions)

  const statelessSliderInstanceTypes = getSupportedSliderInstanceTypes().filter(
    (sliderInstanceType) => sliderInstanceType !== `elasticsearch`,
  )

  const { hasPermissions: hasDeletePermission } = usePermissions([
    { type: 'stack-version', action: 'delete', id: '*' },
  ])

  const columns = [
    {
      label: (
        <FormattedMessage
          id='region-admin-versions-table.elasticsearch-version'
          defaultMessage='Elasticsearch version'
        />
      ),
      render: (version) => (
        <EuiFlexGroup>
          <EuiFlexItem grow={true}>
            <EsVersionLink region={region!} version={version} />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EOLVersionWarningBadge version={version.version} sliderInstanceType='elasticsearch' />
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
      sortKey: `version`,
      width: `220px`,
    },

    {
      label: (
        <FormattedMessage id='region-admin-versions-table.includes' defaultMessage='Includes' />
      ),
      render: (stackPack) => (
        <EuiFlexGroup justifyContent='flexStart' gutterSize='m'>
          {statelessSliderInstanceTypes
            .filter((sliderInstanceType) => stackPack[sliderInstanceType] != null)
            .map((sliderInstanceType) => {
              const logo = (
                <CuiSliderLogo
                  key={sliderInstanceType}
                  sliderInstanceType={sliderInstanceType}
                  version={stackPack.version}
                  showTooltip={true}
                />
              )
              const content =
                stackPack[sliderInstanceType].version !== stackPack.elasticsearch!.version ? (
                  <EuiFlexGroup justifyContent='flexStart' gutterSize='xs'>
                    <EuiFlexItem grow={false}>{logo}</EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiBadge color='hollow'>v{stackPack[sliderInstanceType].version}</EuiBadge>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                ) : (
                  logo
                )
              return (
                <EuiFlexItem key={sliderInstanceType} grow={false}>
                  {content}
                </EuiFlexItem>
              )
            })}
        </EuiFlexGroup>
      ),
    },

    ...(isWhitelistingEnabled
      ? [
          {
            label: (
              <FormattedMessage id='region-admin-versions-table.allowed' defaultMessage='Allowed' />
            ),
            render: (version) => <WhitelistSwitch versionInfo={version} region={region!} />,
          },
        ]
      : []),

    {
      label: <FormattedMessage id='region-admin-versions-table.actions' defaultMessage='Actions' />,
      id: `deleteButton`,
      css: deleteButtonStyles,
      render: (version) => (
        <DeleteVersionButton
          region={region!}
          version={version}
          fetchVersions={fetchVersions}
          deleteVersion={deleteVersion}
          deleteVersionRequest={getDeleteVersionRequest(region!.id, version.version)}
          hasDeletePermission={hasDeletePermission}
        />
      ),
      width: `150px`,
    },
  ]

  return (
    <CuiTable
      data-test-id='elastic-stack-versions-table'
      rows={sortedVersions}
      getRowId={(stackVersionConfig) => stackVersionConfig.version}
      columns={columns}
      emptyMessage={
        <FormattedMessage
          id='region-admin-versions-table.no-versions-yet'
          defaultMessage='No Elastic Stack versions yet!'
        />
      }
      initialLoading={!region}
    />
  )
}

function DeleteVersionButton({
  region,
  version,
  fetchVersions,
  deleteVersion,
  deleteVersionRequest,
  hasDeletePermission,
}: {
  region: Region
  version: StackVersionConfig
  fetchVersions: (regionId: string) => Promise<unknown>
  deleteVersion: (version: string, region: Region) => Promise<unknown>
  deleteVersionRequest: AsyncRequestState
  hasDeletePermission: boolean
}) {
  const onClick = () => deleteVersion(version.version!, region).then(() => fetchVersions(region.id))

  return (
    <DangerButton
      disabled={!hasDeletePermission}
      data-test-id='elastic-stack-delete-stack'
      size='s'
      color='danger'
      onConfirm={onClick}
      modal={{
        title: (
          <FormattedMessage
            id='delete-stack-version.delete'
            defaultMessage='Delete Elastic Stack version'
          />
        ),
        body: (
          <FormattedMessage
            id='delete-stack-version.confirm-to-delete'
            defaultMessage='Delete Elastic Stack version { version }?'
            values={{
              version: version.version,
            }}
          />
        ),
      }}
      isBusy={deleteVersionRequest.inProgress}
    >
      <span>
        <FormattedMessage id='region-admin-versions-table.delete-version' defaultMessage='Delete' />
      </span>
    </DangerButton>
  )
}

export default VersionTable

function EsVersionLink({ region, version }) {
  return (
    <CuiLink to={elasticStackVersionUrl(region.id, version.version)}>
      <EuiText size='m'>v{version.version}</EuiText>
    </CuiLink>
  )
}

function compareVersions(a, b) {
  if (a.version == null || !valid(a.version!) || b.version == null || !valid(b.version)) {
    // if we can't compare the order just keep it as it is
    return 1
  }

  const compare = rcompare(a.version!, b.version)

  return compare
}
